import {
  DatePeriodFilter,
  DurationFilter,
  MatchType,
  MediaType,
  SearchField,
  SearchType,
} from 'utils/enum';
import { AdvancedSearchParams } from 'utils/models';
import { values, without } from 'lodash';
import { SEMANTIC_LANGUAGES } from './semanticSearchQuery';

type FilterSupport = {
  uploadDates: DatePeriodFilter[];
  customDate: boolean;
  types: MediaType[];
  durations: DurationFilter[];
  /** Includes: chips (`searchValues`) under one of these `searchField`s. */
  searchFields: SearchField[];
  searchBy: SearchType[];
  matchTypes: MatchType[];
  languages: string[];
  excludeWords: boolean;
};

/** Both modes share one filter pool (`advancedSearch.searchParams` and the
 * collection slice). This table lists what each mode can use today: FilterBar
 * shows only these options, and `pickSupportedFilters` sends only these
 * values. Speakers and collections work in both modes, so they are not listed.
 * Normal search's backend has no "Yesterday" (library/index.py process_date). */
export const FILTER_SUPPORT: Record<'normal' | 'semantic', FilterSupport> = {
  normal: {
    uploadDates: [
      DatePeriodFilter.TODAY,
      DatePeriodFilter.THIS_WEEK,
      DatePeriodFilter.THIS_MONTH,
      DatePeriodFilter.THIS_YEAR,
    ],
    customDate: true,
    types: values(MediaType),
    durations: values(DurationFilter),
    searchFields: values(SearchField),
    searchBy: [SearchType.TITLE],
    matchTypes: [],
    languages: [],
    excludeWords: false,
  },
  semantic: {
    uploadDates: [
      DatePeriodFilter.TODAY,
      DatePeriodFilter.YESTERDAY,
      DatePeriodFilter.THIS_WEEK,
      DatePeriodFilter.THIS_MONTH,
      DatePeriodFilter.THIS_YEAR,
    ],
    customDate: true,
    types: values(MediaType),
    durations: values(DurationFilter),
    // Every Includes layer the library offers; transcripts is not one of them.
    searchFields: without(values(SearchField), SearchField.TRANSCRIPTS),
    searchBy: [SearchType.TITLE],
    matchTypes: [MatchType.EXACT_PHRASE],
    languages: SEMANTIC_LANGUAGES.map(({ value }) => value),
    excludeWords: true,
  },
};

export const filterSupport = (isSemanticSearch: boolean): FilterSupport =>
  FILTER_SUPPORT[isSemanticSearch ? 'semantic' : 'normal'];

const keep = <T>(value: T | null | undefined, allowed: T[]): T | null =>
  value != null && allowed.includes(value) ? value : null;

/** The pool as the given mode sees it: a value the mode does not support is
 * dropped, so it is neither sent nor counted as an active filter. The pool
 * itself keeps it for the other mode. */
export const pickSupportedFilters = (
  params: AdvancedSearchParams,
  isSemanticSearch: boolean,
): AdvancedSearchParams => {
  const support = filterSupport(isSemanticSearch);
  // The normal request falls back to Key terms when no field is picked.
  const searchField = keep(
    (params.searchField ?? SearchField.KEYWORD) as SearchField,
    support.searchFields,
  );

  return {
    ...params,
    uploadDate: keep(params.uploadDate, support.uploadDates),
    customDate: support.customDate ? params.customDate : null,
    type: keep(params.type, support.types),
    duration: keep(params.duration, support.durations),
    searchField,
    searchValues: searchField ? params.searchValues : [],
    searchBy: keep(params.searchBy as SearchType | null, support.searchBy),
    matchType: keep(params.matchType as MatchType | null, support.matchTypes),
    languages: (params.languages ?? []).filter((language) =>
      support.languages.includes(language),
    ),
    excludeWords: support.excludeWords ? params.excludeWords ?? [] : [],
  };
};

/** True when the mode would send at least one filter. */
export const hasSupportedFilters = (
  params: AdvancedSearchParams,
  isSemanticSearch: boolean,
): boolean => {
  const picked = pickSupportedFilters(params, isSemanticSearch);
  return !!(
    picked.uploadDate ||
    picked.customDate ||
    picked.type ||
    picked.duration ||
    picked.searchBy ||
    picked.matchType ||
    picked.speakerNames?.length ||
    picked.searchValues?.length ||
    picked.languages.length ||
    picked.excludeWords.length
  );
};
